import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getReadingList(
    $filter: readingFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getReadingList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        vincenseReading {
          id
          pulseFlag
          pulseColor
          pulseValue
          pulseReferenceRange
          o2Flag
          o2Color
          o2Value
          o2ReferenceRange
          rrFlag
          rrColor
          rrValue
          rrReferenceRange
          skinValue
        }
        bpReading {
          id
          systolicFlag
          systolicColor
          systolicValue
          systolicReferenceRange
          diastolicFlag
          diastolicColor
          diastolicValue
          diastolicReferenceRange
          meanFlag
          meanColor
          meanValue
          meanReferenceRange
        }
        glucometerReading {
          id
          randomFlag
          randomColor
          randomValue
          randomReferenceRange
          fastingFlag
          fastingColor
          fastingValue
          fastingReferenceRange
          ppFlag
          ppColor
          ppValue
          ppReferenceRange
        }
        weighingReading {
          id
          weightValue
          heightValue
          bmiFlag
          bmiColor
          bmiValue
          bmiReferenceRange
        }
        ecgReading {
          id
          ecgUrl
          pdfUrl
        }
        ishReading {
          id
          score
          color
          referenceRange
        }
        ewsReading {
          id
          readingId
          pulseValue
          pulseScore
          pulseColor
          skinValue
          skinScore
          skinColor
          o2Value
          o2Score
          o2Color
          rrValue
          rrScore
          rrColor
          systolicValue
          systolicScore
          systolicColor
          avpuValue
          avpuScore
          avpuColor
          uoValue
          uoScore
          uoColor
          updatedById
          updatedBy {
            id
            name
            code
          }
        }
        readingRisk {
          id
          readingId
          hypertensionRisk
          hypotensionRisk
          diabeticRisk
          cardiacRisk
          obesityRisk
          respiratoryRisk
          covidRisk
          riskCount
        }
        subject {
          id
          subjectType
          name
          gender
          dob
          photo
          organization {
            id
            name
            type
            logoUrl
            level {
              id
              name
            }
          }
          keyValueList {
            id
            value
            organizationSubjectKey {
              id
              name
              priorityOrder
            }
          }
        }
        customTestList {
          id
          readingId
          customTestId
          value
          customTest {
            id
            customTestGroupId
            name
            type
            optionList
            orderNumber
            customTestGroup {
              id
              name
              orderNumber
            }
          }
        }
        additionalReportList {
          reportName
          fileUrl
        }
        updateSubjectLocation
        location
        doctorViewedDatetime
        type
        datetime
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.ReadingListState = {
  result: null,
  error: null,
  loading: false
};

export const getReadingListRequest = createAsyncThunk<
  TYPES.ReadingList,
  {
    filter?: TYPES.ReadingFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "reading/get_reading_list",
  async (
  params: {
    filter?: TYPES.ReadingFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getReadingList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const getReadingListSlice = createSlice({
  name: "get_reading_list",
  initialState: initialState,
  reducers: {
    getReadingListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
      state.requestId = undefined;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getReadingListRequest.pending, (state, action) => {
      state.result = null;
      state.error = null;
      state.loading = true;
      state.requestId = action.meta.requestId;
    }).
    addCase(getReadingListRequest.fulfilled, (state, action) => {
      // Only the newest request may answer. A wide date range can take many
      // seconds, so its late response used to replace the rows and count of a
      // narrower range picked after it, leaving a count that matched no filter
      // on screen.
      if (action.meta.requestId !== state.requestId) return;
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getReadingListRequest.rejected, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.error = action.payload as Error || {
        message: "Failed to fetch",
        statusCode: "500"
      };
      state.loading = false;
    });
  }
});

export const { getReadingListClear } = getReadingListSlice.actions;

export const getReadingList = createSelector(
  (state: RootState) => state.reading.getReadingList.result,
  (state: RootState) => state.reading.getReadingList.error,
  (state: RootState) => state.reading.getReadingList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getReadingListSlice.reducer;